import { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useSetRecoilState } from "recoil";
import { doc, onSnapshot } from "firebase/firestore";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useGetLocationsList } from "src/api/locations";
import {
  useActivateEmployeeMutation,
  useAddEmployeeMutation,
  useUpdateEmployeeMutation,
} from "src/api/employees/employees";
import { notificationsState } from "src/store/notifications";
import { db } from "src/firebase";
import { FormModeTypes } from "src/types/generic";
import { ServiceFormInputs } from "../../locations/add-edit-location/add-edit-service-popup";
import DeleteIcon from "src/assets/svg-icons/delete.svg";

const DEVICE_VERSIONS = ["V2", "V2s", "V2 PRO", "V3"] as const;

type DeviceVersion = (typeof DEVICE_VERSIONS)[number];

type DeviceService = {
  id?: string;
  type: string;
  name: string;
  nameEn?: string;
  nameAr?: string;
};

type DeviceLocation = {
  id: string;
  nameEn: string;
  services?: DeviceService[];
};

type DeviceFormValues = {
  deviceSerialNumber: string;
  deviceVersion: DeviceVersion | "";
  email: string;
  locationsServices: {
    locationId: string;
    services: string[];
    locationServicesOptions: DeviceService[];
  }[];
};

const getLocationServiceKey = (service: { type: string; name: string }) =>
  JSON.stringify([service.type, service.name]);

const schema: yup.ObjectSchema<DeviceFormValues> = yup.object({
  deviceSerialNumber: yup
    .string()
    .trim()
    .required("Device serial number is required"),

  deviceVersion: yup
    .mixed<DeviceVersion | "">()
    .oneOf([...DEVICE_VERSIONS, ""])
    .required("Device version is required")
    .test("required-version", "Device version is required", Boolean),

  email: yup.string().email("Invalid email").required("Required"),

  locationsServices: yup
    .array()
    .of(
      yup.object({
        locationId: yup.string().required("Select a location"),
        services: yup
          .array()
          .of(yup.string().required())
          .min(1, "Select at least one service")
          .required(),
        locationServicesOptions: yup
          .array()
          .of(
            yup.object({
              type: yup.string().required(),
              name: yup.string().required(),
            }),
          )
          .required(),
      }),
    )
    .min(1, "Assign at least one location")
    .required(),
});

const defaultValues: DeviceFormValues = {
  deviceSerialNumber: "",
  deviceVersion: "",
  email: "",
  locationsServices: [
    {
      locationId: "",
      services: [],
      locationServicesOptions: [],
    },
  ],
};

const AddEditDevicePage = () => {
  const { id } = useParams();
  const mode: FormModeTypes = id ? "edit" : "add";
  const navigate = useNavigate();
  const location = useLocation();

  const setNotifications = useSetRecoilState(notificationsState);

  const isComingFromActiveEmployeesPage = location.pathname.includes("active");

  const [accountData, setAccountData] = useState<any>(null);
  const [isLoadingFetching, setIsLoadingFetching] = useState(mode === "edit");
  const [isLoading, setIsLoading] = useState(false);

  // Apis
  const { data: locationsData, isLoading: isLoadingLocations } =
    useGetLocationsList({
      type: "active",
      listAll: true,
    });

  const locations: DeviceLocation[] = locationsData?.locations ?? [];

  const { mutate: mutateAdd, isPending: isPendingAdd } =
    useAddEmployeeMutation();

  const { mutate: mutateUpdate, isPending: isPendingUpdate } =
    useUpdateEmployeeMutation();

  const { mutate: mutateActivate, isPending: isPendingActivate } =
    useActivateEmployeeMutation();

  const methods = useForm<DeviceFormValues>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = methods;

  const {
    fields: locationFields,
    append,
    remove,
    replace,
  } = useFieldArray({
    control,
    name: "locationsServices",
  });

  const locationsServices = watch("locationsServices") ?? [];

  const isSubmitting = isLoading || isPendingAdd || isPendingUpdate;

  const getLocationServices = (locationId: string): DeviceService[] => {
    const location = locations.find((item) => item.id === locationId);

    return (location?.services ?? []).map((service) => ({
      type: service.type,
      name: service.name ?? service.nameEn ?? service.type,
    }));
  };

  // Fetch the existing user document when editing.
  useEffect(() => {
    if (!id) {
      setIsLoadingFetching(false);
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, "users", id),
      (snapshot) => {
        if (!snapshot.exists()) {
          setNotifications((previous) => [
            ...previous,
            {
              type: "error",
              message: "Device record was not found.",
            },
          ]);
          setIsLoadingFetching(false);
          return;
        }

        const data = snapshot.data();
        console.log("Fetched user data:", data);
        setAccountData(data);

        reset({
          deviceSerialNumber: data.deviceSerialNumber ?? "",
          deviceVersion: data.deviceVersion ?? "",
          email: data.email ?? "",

          locationsServices: (data.allowedLocationServices ?? []).map(
            (loc: any) => {
              const selectedLocation = locationsData?.locations.find(
                (item: any) => item.id === loc.locationId,
              );

              const availableServices: DeviceService[] = (
                selectedLocation?.services ?? []
              ).map((service: ServiceFormInputs) => ({
                type: service.type,
                name: service.name,
              }));
              const savedServices: DeviceService[] = loc.services ?? [];
              const locationServicesOptions = [
                ...availableServices,
                ...savedServices.filter(
                  (service) =>
                    !availableServices.some(
                      (option) =>
                        getLocationServiceKey(option) ===
                        getLocationServiceKey(service),
                    ),
                ),
              ];
              // Older Sunmi records only stored service types. Select all current
              // services of those types while new records retain the service name.
              const selectedServices = savedServices.length
                ? savedServices
                : availableServices.filter((service) =>
                    (loc.serviceTypes ?? []).includes(service.type),
                  );

              return {
                locationId: loc.locationId,
                services: selectedServices.map(getLocationServiceKey),
                locationServicesOptions,
              };
            },
          ),
        });

        setIsLoadingFetching(false);
      },
      (error) => {
        setNotifications((previous) => [
          ...previous,
          {
            type: "error",
            message: error.message || "Failed to load device data.",
          },
        ]);
        setIsLoadingFetching(false);
      },
    );

    return unsubscribe;
  }, [id, locationsData, reset, setNotifications]);

  // Populate the available service options after locations have loaded.
  useEffect(() => {
    if (!locations.length || !locationsServices.length) return;

    locationsServices.forEach((item, index) => {
      if (!item.locationId) return;

      const liveServices = getLocationServices(item.locationId);
      const savedServices: DeviceService[] =
        accountData?.allowedLocationServices?.find(
          (location: { locationId: string }) =>
            location.locationId === item.locationId,
        )?.services ?? [];
      const locationServicesOptions = [
        ...liveServices,
        ...savedServices.filter(
          (service) =>
            !liveServices.some(
              (option) =>
                getLocationServiceKey(option) ===
                getLocationServiceKey(service),
            ),
        ),
      ];

      setValue(
        `locationsServices.${index}.locationServicesOptions`,
        locationServicesOptions,
      );
    });
  }, [locationsData, locationsServices.length, id, accountData]);

  const handleAddLocation = () => {
    append({
      locationId: "",
      services: [],
      locationServicesOptions: [],
    });
  };

  const handleRemoveLocation = (index: number) => {
    if (locationFields.length === 1) {
      replace([
        {
          locationId: "",
          services: [],
          locationServicesOptions: [],
        },
      ]);
      return;
    }

    remove(index);
  };

  const showNotification = (type: "success" | "error", message: string) => {
    setNotifications((previous) => [
      ...previous,
      {
        type,
        message,
      },
    ]);
  };

  const handleSuccess = () => {
    if (accountData?.isUserActive === false && id) {
      mutateActivate(id, {
        onSuccess: () => {
          showNotification(
            "success",
            "The account has been activated successfully!",
          );
          navigate(-1);
        },
        onError: (error) => {
          showNotification(
            "error",
            error.response?.data?.message ??
              error.message ??
              "Failed to activate the record.",
          );
        },
      });
      return;
    }

    showNotification(
      "success",
      mode === "add"
        ? "The account has been added successfully!"
        : "The account has been updated successfully!",
    );

    setIsLoading(false);

    if (mode === "add") {
      navigate(-1);
      return;
    }
  };

  const handleError = (error: {
    message?: string;
    response?: { data?: { message?: string } };
  }) => {
    setIsLoading(false);

    showNotification(
      "error",
      error.response?.data?.message ??
        error.message ??
        "Oops! Something went wrong.",
    );
  };

  const onSubmit = (data: DeviceFormValues) => {
    setIsLoading(true);

    const allowedLocationServices = data.locationsServices
      .filter((item) => item.locationId)
      .map((item) => {
        const selectedServices = (item.locationServicesOptions ?? []).filter(
          (service) => item.services?.includes(getLocationServiceKey(service)),
        );
        return {
          locationId: item.locationId,
          serviceTypes: Array.from(
            new Set(selectedServices.map((service) => service.type)),
          ),
          services: selectedServices.map(({ type, name }) => ({ type, name })),
        };
      });

    const dataToSend = {
      // Device-specific fields.
      deviceSerialNumber: data.deviceSerialNumber.trim(),
      deviceVersion: data.deviceVersion,
      name: "",
      arabicName: "",
      accountType: "sunmi",
      email: data.email.trim(),
      allowedLocationsId: allowedLocationServices.map(
        (item) => item.locationId,
      ),
      allowedLocationServices,

      // Existing employee fields not used by this form.

      phone: accountData?.phone ?? "",
      phone2: accountData?.phone2 ?? "",
      employeeType: "",
      nationalId: accountData?.nationalId ?? "",
      dateOfBirth: accountData?.dateOfBirth ?? null,
      startDate: accountData?.startDate ?? null,
      endDate: accountData?.endDate ?? null,
      isSupportMode: false,
      roles: accountData?.roles ?? [],
      locationType: accountData?.locationType ?? "",
      permissions: accountData?.permissions ?? {},
      locationShifts: [],
      //   zoneShift: null,
      allowedCityIds: [],
      attachments: accountData?.attachments ?? [],
    };

    if (mode === "add") {
      mutateAdd(dataToSend as any, {
        onSuccess: handleSuccess,
        onError: handleError,
      });
      return;
    }

    mutateUpdate(
      {
        ...dataToSend,
        employeeId: id,
      } as any,
      {
        onSuccess: () => {
          // if (accountData?.isUserActive === false) {
          //   handleUnarchiveEmployee();
          //   return;
          // }
          handleSuccess();
        },
        onError: handleError,
      },
    );
  };

  const selectedLocationWithServicesIds = locationsServices?.map(
    (loc) => loc.locationId,
  );

  const hasSelectedLocations = locationsServices?.some(
    (item) => !!item?.locationId,
  );

  if (isLoadingFetching || isLoadingLocations) {
    return (
      <Box
        height="80vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={{ xs: "15px", md: "30px" }} data-testid="add-edit-device-page">
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Typography fontSize="22px" mb={1} fontWeight={600}>
          {mode === "add" ? "Add New Sunmi Account" : "Edit Sunmi Account"}
        </Typography>

        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ color: "#5E6278" }}
          aria-label="breadcrumb"
        >
          <Link
            key="1"
            color={"#A1A5B7"}
            underline="hover"
            onClick={() => navigate(-1)}
            component="text"
            sx={{ cursor: "pointer" }}
          >
            {isComingFromActiveEmployeesPage ? "Active" : "Archived"} Accounts
          </Link>
          <Typography color="#A1A5B7">
            {mode === "add" ? "Add New Sunmi Account" : "Edit Sunmi Account"}
          </Typography>
        </Breadcrumbs>

        <Box
          mt={3}
          p={{ xs: 2, md: 4 }}
          border="1px solid #5B5B5B"
          borderRadius="10px"
        >
          <Typography mb={3}>Account Info</Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Controller
                name="deviceSerialNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Device Serial Number"
                    fullWidth
                    size="small"
                    error={Boolean(errors.deviceSerialNumber)}
                    helperText={errors.deviceSerialNumber?.message}
                    disabled={isSubmitting}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                size="small"
                error={Boolean(errors.deviceVersion)}
              >
                <InputLabel id="device-version-select-label">
                  Device Version
                </InputLabel>
                <Controller
                  name="deviceVersion"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      label="Device Version"
                      labelId="device-version-select-label"
                      id="device-version-select"
                      disabled={isSubmitting}
                    >
                      {DEVICE_VERSIONS.map((version) => (
                        <MenuItem key={version} value={version}>
                          {version}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <FormHelperText>{errors.deviceVersion?.message}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    size="small"
                    label="User Name"
                    {...field}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    fullWidth
                    InputLabelProps={{
                      shrink: field.value !== undefined,
                    }}
                    disabled={mode === "edit"}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Box>
            <Typography mb={2}>Assign Locations</Typography>
            <Stack gap={3}>
              {locationFields.map((locServicesField, index) => {
                const fieldLocationId =
                  watch("locationsServices")?.[index]?.locationId;
                return (
                  <Stack
                    key={locServicesField.id}
                    direction={"row"}
                    gap={2}
                    alignItems={"flex-start"}
                    width="100%"
                  >
                    <FormControl
                      size="small"
                      sx={{ flex: "5 1 0", minWidth: 0 }}
                    >
                      <Controller
                        name={`locationsServices.${index}.locationId`}
                        control={control}
                        render={({ field }) => (
                          <Autocomplete
                            // loading={true}
                            size="small"
                            options={
                              (locationsData?.locations ?? []).filter(
                                (item: any) =>
                                  !selectedLocationWithServicesIds?.includes(
                                    item.id,
                                  ) || item.id === field.value,
                              ) ?? []
                            }
                            value={
                              (locationsData?.locations ?? []).find(
                                (option: any) => option.id === field.value,
                              ) ?? null
                            }
                            isOptionEqualToValue={(option, value) =>
                              option.id === value.id
                            }
                            getOptionLabel={(option: any) => option?.nameEn}
                            getOptionKey={(option: any) => option.id}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                error={
                                  !!errors.locationsServices?.[index]
                                    ?.locationId
                                }
                                helperText={
                                  errors.locationsServices?.[index]?.locationId
                                    ?.message
                                }
                                label={"Location"}
                              />
                            )}
                            renderOption={(props, option: any) => (
                              <li {...props} key={option.id}>
                                <Typography>{option.nameEn}</Typography>
                              </li>
                            )}
                            onChange={(_, data) => {
                              field.onChange(data?.id ?? null);

                              // to set services options
                              const services: DeviceService[] =
                                data?.services?.map(
                                  (service: ServiceFormInputs) => ({
                                    type: service.type,
                                    name: service.name,
                                  }),
                                ) ?? [];

                              setValue(
                                `locationsServices.${index}.locationServicesOptions`,
                                services,
                              );
                              // to reset services selected when location changes
                              setValue(
                                `locationsServices.${index}.services`,
                                [],
                              );
                            }}
                          />
                        )}
                      />
                    </FormControl>

                    <Stack
                      direction="row"
                      gap={1}
                      alignItems="flex-start"
                      sx={{ flex: "7 1 0", minWidth: 0 }}
                    >
                      <FormControl
                        fullWidth
                        size="small"
                        error={
                          !!errors.locationsServices?.[index]?.services &&
                          !!fieldLocationId
                        }
                        sx={{ minWidth: 0, display: "block" }}
                        disabled={!fieldLocationId}
                      >
                        <InputLabel id="demo-multiple-checkbox-label">
                          Select Services
                        </InputLabel>
                        <Controller
                          name={`locationsServices.${index}.services`}
                          control={control}
                          defaultValue={[]}
                          render={({ field }) => (
                            <Select
                              {...field}
                              fullWidth
                              labelId="demo-multiple-checkbox-label"
                              id="demo-multiple-checkbox"
                              multiple
                              input={
                                <OutlinedInput label={"Select Services"} />
                              }
                              renderValue={(selected) => (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 0.5,
                                  }}
                                >
                                  {selected.map((key) => {
                                    const service = watch(
                                      `locationsServices.${index}.locationServicesOptions`,
                                    )?.find(
                                      (option) =>
                                        getLocationServiceKey(option) === key,
                                    );
                                    return (
                                      <Chip
                                        size="small"
                                        key={key}
                                        label={service?.name ?? key}
                                      />
                                    );
                                  })}
                                </Box>
                              )}
                            >
                              {(
                                watch("locationsServices")?.[index]
                                  .locationServicesOptions ?? []
                              ).map((service) => (
                                <MenuItem
                                  key={getLocationServiceKey(service)}
                                  value={getLocationServiceKey(service)}
                                >
                                  <Checkbox
                                    size="small"
                                    checked={field.value?.includes(
                                      getLocationServiceKey(service),
                                    )}
                                  />
                                  {service.name}
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                        />
                        <FormHelperText id="my-helper-text">
                          {!!fieldLocationId &&
                            errors.locationsServices?.[index]?.services
                              ?.message}
                        </FormHelperText>
                      </FormControl>
                      {locationFields.length > 1 && (
                        <IconButton
                          onClick={() => {
                            handleRemoveLocation(index);
                          }}
                        >
                          <img src={DeleteIcon} alt="" />
                        </IconButton>
                      )}
                    </Stack>
                  </Stack>
                );
              })}

              {locationFields.length >= 4 ? null : (
                <Button
                  variant="outlined"
                  onClick={handleAddLocation}
                  sx={{ width: "fit-content" }}
                >
                  {hasSelectedLocations
                    ? "Assign Another Location"
                    : "Assign Location"}
                </Button>
              )}
            </Stack>
          </Box>
        </Box>

        <Stack direction="row" justifyContent="flex-end" mt={5} gap={3}>
          <Button
            variant="outlined"
            sx={{ minWidth: { xs: 100, md: 208 } }}
            onClick={() => navigate(-1)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>

          <LoadingButton
            type="submit"
            variant="contained"
            sx={{ minWidth: { xs: 100, md: 208 } }}
            loading={isSubmitting || isPendingActivate}
          >
            Save
          </LoadingButton>
        </Stack>
      </form>
    </Box>
  );
};

export default AddEditDevicePage;
